import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BoardCallSplitLayout } from "../components/board/BoardCallSplitLayout";
import { BoardParticipants, type BoardParticipant } from "../components/board/BoardParticipants";
import { BoardEmptyHint } from "../components/board/BoardEmptyHint";
import { BoardFloatingToolbar } from "../components/board/BoardFloatingToolbar";
import { BoardSelectionToolbar } from "../components/board/BoardSelectionToolbar";
import { TextOverlayEditor } from "../components/board/TextOverlayEditor";
import { ObjectCommentsPanel } from "../components/board/ObjectCommentsPanel";
import { PresentationOverlay } from "../components/board/PresentationOverlay";
import { PdfImportProgress } from "../components/board/PdfImportProgress";
import { usePdfPresentation } from "../hooks/usePdfPresentation";
import { useBoardCallSession } from "../hooks/useBoardCallSession";
import type { BoardToolId } from "../editor/types";
import { copyStyleFromSelection, pasteStyleToSelection } from "../editor/styleClipboard";
import { lineAxisSnapEnabled, setLineAxisSnapEnabled } from "../editor/lineSnapPrefs";
import { useBoardToolShortcuts, isTypingTarget } from "../hooks/useBoardToolShortcuts";
import { FlashStack, useFlashStack } from "../components/FlashStack";
import { useBoardViewport } from "../hooks/useBoardViewport";
import { useBoardChromeQuiet } from "../hooks/useBoardChromeQuiet";
import { useFollowPresenter } from "../hooks/useFollowPresenter";
import { BoardZoomControls } from "../components/board/BoardZoomControls";
import { BoardMinimap, contentBoundsForFit } from "../components/board/BoardMinimap";
import {
  readMinimapVisibleDefault,
  writeMinimapVisible,
} from "../lib/board/minimapPrefs";
import { BoardHelpButton } from "../components/board/BoardHelpButton";
import { BoardSettingsDialog } from "../components/board/BoardSettingsDialog";
import { BoardFramesButton } from "../components/board/BoardFramesButton";
import { BoardPageMenu } from "../components/board/BoardPageMenu";
import { BoardFramePresentBar } from "../components/board/BoardFramePresentBar";
import { BoardVotePanel } from "../components/board/BoardVotePanel";
import { BoardBugReportDialog } from "../components/board/BoardBugReportDialog";
import { useFramePresentation } from "../hooks/useFramePresentation";
import { useBoardVote, VOTE_HEIGHT_DEFAULT, VOTE_WIDTH_DEFAULT } from "../hooks/useBoardVote";
import { useSpaceGesture } from "../hooks/useSpaceGesture";
import { cycleActivePenDash } from "../editor/penDash";
import { frameTitle, isFrameObject } from "../editor/frame/frameContext";
import { noteDiagBoardError, setBoardDiagSceneGetter } from "../lib/board/boardDiagTrail";
import { handleBoardClipboardPaste } from "../lib/board/handleBoardClipboardPaste";
import { handleBoardClipboardCopy } from "../lib/board/handleBoardClipboardCopy";
import { WbBoardScene } from "../lib/board/wb-board-scene";
import { WbBoardPresence } from "../lib/board/wb-board-presence";
import type { BoardObjectV2, BoardTemplateId } from "@bezpart-board/shared";
import { buildWidgetObjectsAtCenter } from "../lib/board/insertBoardWidget";
import { isBoardAudioFile } from "../lib/audio/importAudioToBoard";
import {
  isPresentationFile,
  presentationProgressLabel,
} from "../lib/pdf/importPresentationToBoard";
import { downloadPresentationPdf } from "../lib/pdf/downloadPresentationPdf";

type GuestBoardResponse = {
  board: { id: string; title: string };
  snapshot: { revision: number; scene: unknown };
  canEdit: boolean;
};

export function GuestBoardPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const guestKey = searchParams.get("k");
  const stageRef = useRef<HTMLDivElement>(null);
  const boardMainRef = useRef<HTMLElement | null>(null);
  const sceneRef = useRef<InstanceType<typeof WbBoardScene> | null>(null);
  const presenceRef = useRef<InstanceType<typeof WbBoardPresence> | null>(null);
  const flash = useFlashStack();

  const [title, setTitle] = useState("Гостевая доска");
  const [ready, setReady] = useState(false);
  const [tool, setTool] = useState<BoardToolId>("select");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [chromeLayoutTick, setChromeLayoutTick] = useState(0);
  const [historyTick, setHistoryTick] = useState(0);
  const [isSelectionDragging, setIsSelectionDragging] = useState(false);
  const [textEditId, setTextEditId] = useState<string | null>(null);
  const [commentObjectId, setCommentObjectId] = useState<string | null>(null);
  const pdfPresentation = usePdfPresentation(sceneRef, ready);
  const [pdfImportMessage, setPdfImportMessage] = useState<string | null>(null);
  const [participants, setParticipants] = useState<BoardParticipant[]>([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [compactHeader, setCompactHeader] = useState(false);
  const toolbarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const update = () => {
      const narrowViewport = window.matchMedia("(max-width: 1200px)").matches;
      setCompactHeader(narrowViewport || el.clientWidth < 980);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);
  const [minimapVisible, setMinimapVisible] = useState(readMinimapVisibleDefault);

  const vp = useBoardViewport();
  useBoardChromeQuiet(boardMainRef, ready);
  const vpRef = useRef(vp);
  vpRef.current = vp;
  const follow = useFollowPresenter(vp, presenceRef, ready, participants);
  const onPeerPresenceRef = useRef(follow.onPeerPresence);
  onPeerPresenceRef.current = follow.onPeerPresence;
  const onSignalRef = useRef<(ev: { kind: string; payload: Record<string, unknown>; peerId: string | null; displayLabel: string | null; ts: number; isSelf: boolean }) => void>(() => {});
  const onPeerJoinedRef = useRef<() => void>(() => {});
  const onSubscribedRef = useRef<() => void>(() => {});

  useEffect(() => {
    document.body.classList.add("wb-page--board");
    return () => document.body.classList.remove("wb-page--board");
  }, []);

  useEffect(() => {
    setBoardDiagSceneGetter(() => {
      const scene = sceneRef.current as
        | (NonNullable<typeof sceneRef.current> & {
            store?: { getRevision?: () => number };
          })
        | null;
      if (!scene) return null;
      try {
        return {
          revision: scene.store?.getRevision?.() ?? 0,
          objects: scene.getObjects() ?? [],
        };
      } catch {
        return null;
      }
    });
    return () => setBoardDiagSceneGetter(null);
  }, [ready, id]);

  const callSession = useBoardCallSession(
    id && guestKey
      ? `/api/v1/boards/${encodeURIComponent(id)}/call-session?k=${encodeURIComponent(guestKey)}`
      : null
  );

  useEffect(() => {
    if (!id || !guestKey || !stageRef.current) return;

    let cancelled = false;
    const stageEl = stageRef.current;

    void fetch(
      `/api/v1/guest/boards/${encodeURIComponent(id)}?k=${encodeURIComponent(guestKey)}`
    )
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (!ok) throw new Error(data.error || "Нет доступа");
        const payload = data as GuestBoardResponse;
        setTitle(payload.board.title || "Гостевая доска");
        document.title = `${payload.board.title || "Доска"} — Bezpart Board`;
        setReady(true);

        const onError = (msg: string) => {
          const codeMatch = msg.match(/\[code=([^ ·\]]+)/);
          const ctxMatch = msg.match(/\[([^ ·\]]+?) · code=/);
          if (codeMatch) {
            noteDiagBoardError({
              text: msg,
              code: codeMatch[1],
              context: ctxMatch ? ctxMatch[1] : undefined,
            });
          }
          flash.push("error", msg);
        };
        const boardScene = new WbBoardScene({
          stage: stageEl,
          boardId: payload.board.id,
          guestEditKey: guestKey,
          useCredentials: false,
          canEdit: true,
          scene: payload.snapshot?.scene,
          revision: payload.snapshot?.revision ?? 0,
          mapClientToStage: vp.mapClientToStageStable,
          toolMode: tool,
          onSelectionChange: setSelectedIds,
          onSelectionChromeLayout: () => setChromeLayoutTick((n) => n + 1),
          onHistoryChange: () => setHistoryTick((n) => n + 1),
          onTextEditRequest: setTextEditId,
          onActiveToolChange: setTool,
          onRevision: () => {},
          panBy: vp.panBy,
          endPanGesture: vp.endPanGesture,
          onError,
        });
        boardScene.mount();
        sceneRef.current = boardScene;

        const presenceCanvas =
          stageEl.closest(".wb-board-viewport") ?? stageEl;
        const presence = new WbBoardPresence({
          boardId: payload.board.id,
          guestEditKey: guestKey,
          canEditOps: true,
          canvas: presenceCanvas as HTMLDivElement,
          mapClientToStage: vp.mapClientToStageStable,
          getViewport: () => {
            const live = vpRef.current.getLiveViewport?.() ?? vpRef.current.viewport;
            return {
              panX: live.panX,
              panY: live.panY,
              scale: live.scale,
            };
          },
          onPeerPresence: (
            peerId: string,
            data: {
              viewport?: { panX: number; panY: number; scale: number } | null;
              cursor?: { x: number; y: number } | null;
            }
          ) => {
            onPeerPresenceRef.current(peerId, data);
          },
          onSignal: (ev: {
            peerId: string | null;
            displayLabel: string | null;
            kind: string;
            payload: Record<string, unknown>;
            ts: number;
            isSelf: boolean;
          }) => {
            onSignalRef.current(ev);
          },
          onPeerJoined: () => {
            onPeerJoinedRef.current();
          },
          onSubscribed: () => {
            onSubscribedRef.current();
          },
          onRosterChange: setParticipants,
          onPeersChange: () => {},
          onStatus: () => {},
        });
        boardScene.bindRealtime(presence);
        presence.attach();
        presenceRef.current = presence;
      })
      .catch((e) => {
        if (!cancelled) flash.push("error", e instanceof Error ? e.message : "Ошибка");
      });

    return () => {
      cancelled = true;
      presenceRef.current?.destroy();
      sceneRef.current?.destroy();
    };
  }, [id, guestKey]);

  useEffect(() => {
    if (!ready) return;
    sceneRef.current?.setActiveTool(tool, false);
  }, [tool, ready]);

  const handleToolChange = useCallback((next: BoardToolId) => {
    setTool(next);
    sceneRef.current?.setActiveTool(next, true);
  }, []);

  useEffect(() => {
    function onOpenComments(ev: Event) {
      const ce = ev as CustomEvent<{ objectId?: string }>;
      const id = ce.detail?.objectId;
      if (id) setCommentObjectId(id);
    }
    window.addEventListener("wb:open-object-comments", onOpenComments as EventListener);
    return () =>
      window.removeEventListener("wb:open-object-comments", onOpenComments as EventListener);
  }, []);

  useEffect(() => {
    function onSelectionDrag(ev: Event) {
      const ce = ev as CustomEvent<boolean>;
      setIsSelectionDragging(Boolean(ce.detail));
    }
    window.addEventListener("wb:selection-drag", onSelectionDrag as EventListener);
    return () => window.removeEventListener("wb:selection-drag", onSelectionDrag as EventListener);
  }, []);

  useEffect(() => {
    if (ready) vp.centerOnOrigin();
  }, [ready, vp.centerOnOrigin]);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      presenceRef.current?.notifyViewportChange();
    }, 80);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ready, vp.viewport.panX, vp.viewport.panY, vp.viewport.scale]);

  useEffect(() => {
    if (!ready) return;
    const needsOverlayTick = Boolean(textEditId || commentObjectId);
    if (!needsOverlayTick) return;
    const t = window.setTimeout(() => {
      setChromeLayoutTick((n) => n + 1);
    }, 320);
    return () => clearTimeout(t);
  }, [ready, vp.viewport.panX, vp.viewport.panY, vp.viewport.scale, textEditId, commentObjectId]);

  useSpaceGesture({
    enabled: ready,
    activeTool: tool,
    onToolChange: handleToolChange,
  });

  useBoardToolShortcuts({
    enabled: ready,
    activeTool: tool,
    onToolChange: handleToolChange,
    selectedIds,
    onDeleteSelection: () => void sceneRef.current?.deleteSelected(),
    onUndo: () => void sceneRef.current?.undo(),
    onRedo: () => void sceneRef.current?.redo(),
    onDuplicate: () => void sceneRef.current?.duplicateSelection(),
    onCopySelection: () => sceneRef.current?.copySelection(),
    onToggleLineAxisSnap: () => {
      const on = !lineAxisSnapEnabled();
      setLineAxisSnapEnabled(on);
      flash.push(
        "info",
        on
          ? "Прямые снова прилипают к горизонтали и вертикали (G — выключить)"
          : "Прилипание выключено: прямые ложатся под любым углом (G — включить)"
      );
    },
    onCopyStyle: () => flash.push("info", copyStyleFromSelection(sceneRef.current, selectedIds)),
    onPasteStyle: () => {
      const msg = pasteStyleToSelection(sceneRef.current, selectedIds);
      if (msg) flash.push("info", msg);
    },
    onPaste: () => void sceneRef.current?.pasteClipboard(),
    onZOrderForward: () => void sceneRef.current?.adjustZOrder("front"),
    onZOrderBackward: () => void sceneRef.current?.adjustZOrder("back"),
    onGroupSelection: () => void sceneRef.current?.groupSelection?.(),
    onUngroupSelection: () => void sceneRef.current?.ungroupSelection?.(),
    // Смена вида линии незаметна на панели, поэтому называем её словом.
    onCyclePenDash: () => void cycleActivePenDash(),
  });

  useEffect(() => {
    if (!ready) return;
    function switchToSelect() {
      const scene = sceneRef.current;
      if (!scene) return;
      scene.setActiveTool("select", true);
      setTool("select");
    }
    function onPaste(ev: ClipboardEvent) {
      if (isTypingTarget(ev.target)) return;
      const scene = sceneRef.current;
      if (!scene) return;
      handleBoardClipboardPaste(ev, scene, { switchToSelect });
    }
    function onCopy(ev: ClipboardEvent) {
      if (isTypingTarget(ev.target)) return;
      handleBoardClipboardCopy(ev, sceneRef.current);
    }
    window.addEventListener("paste", onPaste);
    window.addEventListener("copy", onCopy);
    return () => {
      window.removeEventListener("paste", onPaste);
      window.removeEventListener("copy", onCopy);
    };
  }, [ready]);

  const objects = ready ? sceneRef.current?.getObjects() : null;
  const boardIsEmpty = Boolean(objects && objects.length === 0);
  void historyTick;
  const canUndo = ready && Boolean(sceneRef.current?.canUndo());
  const canRedo = ready && Boolean(sceneRef.current?.canRedo());

  const handleImageFile = useCallback((file: File) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.uploadImageFile(file, pt.x, pt.y);
  }, []);

  const handlePdfFile = useCallback((file: File) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    setPdfImportMessage("Загрузка…");
    void scene
      .uploadPdfFile(file, pt.x, pt.y, (p) => {
        setPdfImportMessage(presentationProgressLabel(p));
      })
      .then(() => setPdfImportMessage(null))
      .catch(() => setPdfImportMessage(null));
  }, []);

  const handleAudioFile = useCallback((file: File) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.uploadAudioFile(file, pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, []);

  const handleDownloadPresentation = useCallback(async (objectId: string) => {
    const scene = sceneRef.current;
    if (!scene) return;
    try {
      await downloadPresentationPdf(scene, objectId);
      flash.push("ok", "Презентация сохранена");
    } catch (e) {
      flash.push("error", e instanceof Error ? e.message : "Не удалось скачать презентацию");
    }
  }, [flash]);

  const handleInsertWidget = useCallback((templateId: BoardTemplateId) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    const objects = buildWidgetObjectsAtCenter(
      templateId,
      pt.x,
      pt.y,
      scene.getObjects() as BoardObjectV2[]
    );
    if (!objects.length) return;
    void scene
      .addObjectsBatch(objects, `widget-${templateId}-${Date.now()}`)
      .then(() => {
        scene.setSelection(objects.map((o) => o.id));
        scene.setActiveTool("select", true);
        setTool("select");
      });
  }, []);

  const handleInsertTimer = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.addTimerAt(pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, []);

  const handleInsertCalculator = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.addCalculatorAt(pt.x, pt.y, { variant: "calc" }).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, []);

  const handleInsertGraphUnits = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.addCalculatorAt(pt.x, pt.y, { variant: "graph_units" }).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, []);

  const handleInsertCode = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.addCodeAt(pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, []);

  const handleInsertSql = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = scene.stageCenterPoint();
    void scene.addSqlAt(pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, []);

  const navigateToFrame = useCallback(
    (frameId: string, opts?: { select?: boolean }) => {
      const scene = sceneRef.current;
      if (!scene) return;
      follow.clearFollow();
      const obj = scene.findObject(frameId);
      if (!obj || obj.type !== "frame") return;
      const w = typeof obj.w === "number" && obj.w > 0 ? obj.w : 640;
      const h = typeof obj.h === "number" && obj.h > 0 ? obj.h : 400;
      const x = typeof obj.x === "number" ? obj.x : 0;
      const y = typeof obj.y === "number" ? obj.y : 0;
      const select = opts?.select !== false;
      if (select) {
        scene.setActiveTool("select", true);
        setTool("select");
        scene.setSelection([frameId]);
      } else {
        scene.setSelection([]);
      }
      const pad = window.matchMedia("(max-width: 720px)").matches ? 28 : 72;
      vp.fitToWorldBounds({ x, y, w, h }, pad);
      vp.viewportRef.current?.focus();
    },
    [follow, vp]
  );

  const framePresent = useFramePresentation({
    presenceRef,
    sceneRef,
    canPresent: ready,
    ready,
    navigateToFrame,
  });
  const boardVote = useBoardVote({ presenceRef, canHost: ready, myUserId: null });

  /** Опрос поверх доски — тот же, что и у хозяина занятия. */
  const handleInsertPoll = useCallback(() => {
    const scene = sceneRef.current;
    const pt = scene ? scene.stageCenterPoint() : null;
    // Опрос встаёт по центру экрана: его место — левый верхний угол, а
    // размер задан в точках экрана, поэтому сдвигаем на половину размера.
    const scale = vp.getLiveViewport().scale || 1;
    const at = pt
      ? {
          worldX: pt.x - VOTE_WIDTH_DEFAULT / 2 / scale,
          worldY: pt.y - VOTE_HEIGHT_DEFAULT / 2 / scale,
        }
      : undefined;
    if (boardVote.startOrQueue(at) === "queued") {
      flash.push("info", "Доска переподключается — опрос появится, как только связь восстановится");
    }
  }, [boardVote, flash, vp]);
  onSignalRef.current = (ev) => {
    framePresent.handleSignal(ev);
    boardVote.handleSignal(ev);
  };
  onPeerJoinedRef.current = () => boardVote.onPeerJoined();
  onSubscribedRef.current = () => boardVote.onSubscribed();

  const presentFrameTitle = (() => {
    if (!framePresent.state.active) return undefined;
    const fid = framePresent.state.frameIds[framePresent.state.index];
    if (!fid) return undefined;
    const obj = sceneRef.current?.findObject(fid);
    return obj && isFrameObject(obj) ? frameTitle(obj) : undefined;
  })();

  function onViewportDrop(ev: React.DragEvent) {
    ev.preventDefault();
    const file = ev.dataTransfer.files?.[0];
    if (!file) return;
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = vp.mapClientToStage(ev.clientX, ev.clientY);
    if (isPresentationFile(file)) {
      setPdfImportMessage("Загрузка…");
      void scene
        .uploadPdfFile(file, pt.x, pt.y, (p) => {
          setPdfImportMessage(presentationProgressLabel(p));
        })
        .then(() => setPdfImportMessage(null))
        .catch(() => setPdfImportMessage(null));
      return;
    }
    if (isBoardAudioFile(file)) {
      void scene.uploadAudioFile(file, pt.x, pt.y).then(() => {
        scene.setActiveTool("select", true);
        setTool("select");
      });
      return;
    }
    if (!file.type.startsWith("image/")) return;
    void scene.uploadImageFile(file, pt.x, pt.y);
  }

  function onViewportDragOver(ev: React.DragEvent) {
    if (ev.dataTransfer.types.includes("Files")) {
      ev.preventDefault();
    }
  }

  if (!id || !guestKey) {
    return (
      <div className="wb-shell">
        <p className="wb-alert wb-alert--error">
          Укажите id и k в адресе (полная ссылка после создания гостевой доски).
        </p>
        <Link className="wb-btn wb-btn--ghost" to="/">
          На главную
        </Link>
      </div>
    );
  }

  return (
    <div className="wb-board-shell" data-testid="board-shell">
      <header
        ref={toolbarRef}
        className={`wb-board-toolbar${compactHeader ? " is-compact" : ""}`}
      >
        <h1 className="wb-board-toolbar__title">{title}</h1>
        <div className="wb-board-toolbar__actions">
          <BoardFramesButton
            className="wb-btn wb-btn--ghost wb-btn--sm"
            ready={ready}
            sceneRef={sceneRef}
            sceneTick={historyTick}
            selectedIds={selectedIds}
            onNavigate={navigateToFrame}
            presentation={
              ready
                ? {
                    canControl: true,
                    active: framePresent.state.active,
                    isPresenter: framePresent.state.isPresenter,
                    disabled: framePresent.state.active && !framePresent.state.isPresenter,
                    onToggle: () => {
                      if (framePresent.state.active && framePresent.state.isPresenter) {
                        framePresent.stop();
                        return;
                      }
                      if (!framePresent.start()) {
                        flash.push("error", "Сначала добавьте хотя бы один фрейм");
                      }
                    },
                  }
                : undefined
            }
          />
          <button
            type="button"
            className="wb-btn wb-btn--ghost wb-btn--sm wb-board-toolbar__action--wide-only"
            onClick={() => setSettingsOpen(true)}
            title="Настройки — горячие клавиши"
          >
            Настройки
          </button>
          <BoardHelpButton
            className="wb-btn wb-btn--ghost wb-btn--sm wb-board-toolbar__action--wide-only"
            open={helpOpen}
            onOpenChange={setHelpOpen}
          />
          <BoardPageMenu
            onOpenHelp={() => setHelpOpen(true)}
            onOpenSettings={() => setSettingsOpen(true)}
            onOpenBugReport={() => setBugReportOpen(true)}
            homeHref="/"
          />
        </div>
      </header>

      {/* Гостевая доска — внешняя аудитория; школьные аккаунты сюда не попадают */}

      <BoardBugReportDialog
        open={bugReportOpen}
        onOpenChange={setBugReportOpen}
        boardId={id}
        defaultReporterName={
          participants.find((p) => p.isSelf)?.displayLabel ?? "Гость"
        }
        context={{
          canEdit: true,
          tool,
          accessMode: "guest",
          sessionAccess: false,
          hasShareToken: false,
        }}
        onSuccess={(reportId) =>
          flash.push(
            "ok",
            reportId ? `Сообщение отправлено (№ ${reportId})` : "Сообщение отправлено"
          )
        }
        onError={(msg) => flash.push("error", msg)}
      />

      <BoardSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <p className="wb-alert wb-alert--ok" style={{ margin: 0, borderRadius: 0 }}>
        Сохраните полный URL с <code>k=</code> — без него редактирование не восстановить.
      </p>

      <div className="wb-board-workspace">
        <BoardCallSplitLayout
          callEmbedUrl={callSession.embedUrl}
          callStatus={callSession.status}
          onRetryCall={callSession.retry}
        >
        <main ref={boardMainRef} className="wb-board-main">
          <BoardFramePresentBar
            active={framePresent.state.active}
            isPresenter={framePresent.state.isPresenter}
            presenterLabel={framePresent.state.presenterLabel}
            index={framePresent.state.index}
            total={framePresent.state.frameIds.length}
            frameTitle={presentFrameTitle}
            onPrev={framePresent.prev}
            onNext={framePresent.next}
            onStop={framePresent.stop}
          />
          <BoardVotePanel
            blocks={boardVote.blocks}
            countsFor={boardVote.countsFor}
            viewportRef={vp.viewportRef}
            viewport={vp.viewport}
            getLiveViewport={vp.getLiveViewport}
            layoutTick={chromeLayoutTick}
            canManage
            isOwnerOf={boardVote.isOwnerOf}
            onCast={boardVote.cast}
            onEnd={boardVote.end}
            onMove={boardVote.move}
            onResize={boardVote.resize}
            onTitleChange={boardVote.setTitle}
            onOptionsChange={boardVote.setOptions}
          />
          <div className="wb-board-status">
            <BoardParticipants
              participants={participants}
              followingPeerId={follow.followPeerId}
              onFollow={ready ? follow.startFollow : undefined}
              onUnfollow={follow.followPeerId ? follow.clearFollow : undefined}
            />
            <span className="wb-ws-badge">guest</span>
          </div>

          <BoardFloatingToolbar
            tool={tool}
            onToolChange={handleToolChange}
            canEdit
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={() => void sceneRef.current?.undo()}
            onRedo={() => void sceneRef.current?.redo()}
            onPaste={() => void sceneRef.current?.pasteClipboard()}
            onImageFile={handleImageFile}
            onPdfFile={handlePdfFile}
            onAudioFile={handleAudioFile}
            onVoiceError={(msg) => flash.push("error", msg)}
            onInsertWidget={handleInsertWidget}
            onInsertTimer={handleInsertTimer}
            onInsertCalculator={handleInsertCalculator}
            onInsertGraphUnits={handleInsertGraphUnits}
            onInsertCode={handleInsertCode}
            onInsertSql={handleInsertSql}
            onInsertPoll={handleInsertPoll}
          />

          <div className="wb-canvas-wrap">
            <BoardEmptyHint visible={boardIsEmpty} canEdit />
            <div
              ref={vp.viewportRef}
              className={`wb-board-viewport${pdfPresentation.activePresentationPdfId ? " is-presenting" : ""}`}
              style={vp.viewportStyle}
              tabIndex={0}
              onDrop={onViewportDrop}
              onDragOver={onViewportDragOver}
              {...vp.handlers}
            >
              <canvas className="wb-board-grid-canvas" aria-hidden="true" />
              <div className="wb-board-viewport__inner" style={vp.innerStyle}>
                <div className="wb-board-world">
                  <div
                    ref={stageRef}
                    className="wb-board-stage"
                    data-testid="board-stage"
                    style={{ display: ready ? "block" : "none" }}
                  />
                </div>
                <BoardSelectionToolbar
                  canEdit
                  activeTool={tool}
                  selectedIds={selectedIds}
                  suppressDuringDrag={framePresent.state.active || isSelectionDragging}
                  editingObjectId={textEditId}
                  sceneRef={sceneRef}
                  viewportRef={vp.viewportRef}
                  viewport={vp.viewport}
                  getLiveViewport={vp.getLiveViewport}
                  layoutTick={chromeLayoutTick}
                  onPresentPdf={pdfPresentation.setLocalPresentationPdfId}
                  onBroadcastPdf={(objectId) => {
                    void sceneRef.current?.startPdfBroadcast(objectId);
                  }}
                  onDownloadPresentation={handleDownloadPresentation}
                  exportBusy={exportBusy}
                  onOpenComments={(objectId) => setCommentObjectId(objectId)}
                  onExportFramePdf={async (objectId) => {
                    const scene = sceneRef.current;
                    if (!scene || exportBusy) return;
                    const frame = scene.findObject(objectId);
                    if (!frame || frame.type !== "frame") return;
                    setExportBusy(true);
                    try {
                      const { exportFrameAsPdf, downloadFramePdf } = await import(
                        "../lib/board/exportFramePdf"
                      );
                      const result = await exportFrameAsPdf({
                        frame,
                        objects: scene.getObjects(),
                        assetContentUrl: (assetId) => scene.assetContentUrl(assetId),
                        credentials: scene.useCredentials ? "include" : "omit",
                      });
                      downloadFramePdf(result.blob, result.filename);
                      flash.push("ok", "PDF рамки сохранён");
                    } catch (e) {
                      flash.push(
                        "error",
                        e instanceof Error ? e.message : "Не удалось сохранить PDF"
                      );
                    } finally {
                      setExportBusy(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {minimapVisible ? (
            <BoardMinimap
              ready={ready}
              objects={ready && sceneRef.current ? sceneRef.current.getObjects() : []}
              sceneTick={historyTick}
              viewport={vp.viewport}
              getLiveViewport={vp.getLiveViewport}
              viewportRef={vp.viewportRef}
              onViewportChange={vp.setViewportState}
              onUserNavigate={() => follow.clearFollow()}
            />
          ) : null}

          <BoardZoomControls
            scale={vp.viewport.scale}
            onZoomIn={() => {
              follow.clearFollow();
              vp.zoomIn();
            }}
            onZoomOut={() => {
              follow.clearFollow();
              vp.zoomOut();
            }}
            onReset={() => {
              follow.clearFollow();
              vp.resetView();
            }}
            onFitContent={() => {
              const scene = sceneRef.current;
              if (!scene) return;
              follow.clearFollow();
              const bounds = contentBoundsForFit(scene.getObjects());
              if (!bounds) {
                vp.resetView();
                return;
              }
              vp.fitToWorldBounds(bounds, 64);
            }}
            canFitContent={ready}
            canZoomIn={vp.canZoomIn}
            canZoomOut={vp.canZoomOut}
            minimapVisible={minimapVisible}
            onToggleMinimap={() => {
              setMinimapVisible((v) => {
                const next = !v;
                writeMinimapVisible(next);
                return next;
              });
            }}
          />
        </main>
        </BoardCallSplitLayout>
      </div>

      <div className="wb-board-flash">
        <FlashStack items={flash.items} onDismiss={flash.dismiss} />
      </div>

      {textEditId && sceneRef.current ? (
        <TextOverlayEditor
          objectId={textEditId}
          scene={sceneRef.current}
          viewportRef={vp.viewportRef}
          viewport={vp.viewport}
          getLiveViewport={vp.getLiveViewport}
          layoutTick={chromeLayoutTick}
          onClose={() => setTextEditId(null)}
          onViewportChange={vp.setViewportState}
        />
      ) : null}

      {commentObjectId && sceneRef.current && !isSelectionDragging ? (
        <ObjectCommentsPanel
          objectId={commentObjectId}
          scene={sceneRef.current}
          viewportRef={vp.viewportRef}
          viewport={vp.viewport}
          layoutTick={chromeLayoutTick}
          sceneTick={historyTick}
          canEdit
          authorLabel={
            participants.find((p) => p.isSelf)?.displayLabel ?? "Участник"
          }
          authorPeerId={presenceRef.current?.myPeerId ?? null}
          onClose={() => setCommentObjectId(null)}
        />
      ) : null}

      {pdfPresentation.activePresentationPdfId && sceneRef.current ? (
        <PresentationOverlay
          objectId={pdfPresentation.activePresentationPdfId}
          scene={sceneRef.current}
          sceneTick={pdfPresentation.sceneTick}
          canEdit
          viewportRef={vp.viewportRef}
          onCloseLocal={() => pdfPresentation.setLocalPresentationPdfId(null)}
          onDownloaded={() => flash.push("ok", "Презентация сохранена")}
          onError={(msg) => flash.push("error", msg)}
        />
      ) : null}

      {pdfImportMessage ? <PdfImportProgress message={pdfImportMessage} /> : null}
    </div>
  );
}
